import { ReactNode, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Close from '@mui/icons-material/Close';
import { Box, Button, Drawer, DrawerProps, Grid, IconButton, Stack, StackProps, Typography } from '@mui/material';

import clsx from 'clsx';

import { convertToTaxonomyCase, EVENTS, useTrack } from '@arrived/analytics';
import { useAuth } from '@arrived/auth';
import { useFilterOptions, useOfferingFilters } from '@arrived/contexts';

import { CombinableFilterOptions } from './CombinableFilterOptions';
import { MarketDropdown } from './MarketDropdown';
import { OfferingFilterChip } from './OfferingFilterChip';
import { PropertyTypeFilterChip } from './PropertyTypeFilterChip';

const DrawerSection = ({ sx, ...rest }: StackProps) => {
  return <Stack sx={{ padding: { sm: 2, md: 0 }, ...sx }} {...rest} />;
};

const FilterSection = ({ children, ...rest }: StackProps) => {
  return (
    <Stack px={{ xs: 3, md: 6 }} py={3} {...rest}>
      {children}
    </Stack>
  );
};

const FilterSectionHeader = ({ children }: { children?: ReactNode }) => {
  return (
    <Typography variant="h6.semibold" pb={2}>
      {children}
    </Typography>
  );
};

const FilterSectionContent = ({ children }: { children?: ReactNode }) => {
  return <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>{children}</Box>;
};

export interface FilterDrawerProps extends DrawerProps {
  close: () => void;
  offeringCount?: number;
}

export const FilterDrawer = ({ close, offeringCount, className, ...rest }: FilterDrawerProps) => {
  const { featuredFilters, featuredFilterIdx, setFeaturedFilterIdx, setOfferingFilter } = useOfferingFilters();
  const { availability, leverage, leaseStatus, tagId, assetType, ownership } = useFilterOptions();
  const track = useTrack();
  const intl = useIntl();
  const { isAuthenticated } = useAuth();

  const handleClearAllOnClick = useCallback(() => setOfferingFilter(), [setOfferingFilter]);
  const handleCancelOnClick = useCallback(() => close(), [close]);

  return (
    <Drawer
      {...rest}
      className={clsx(className, 'filter-drawer')}
      PaperProps={{
        sx: {
          maxWidth: '680px',
          width: '100%',
          height: { md: '100%' },
        },
      }}
      sx={{ zIndex: { xs: 'modal', md: 'drawer' } }}
    >
      <Grid item container direction="column" position="relative" flexGrow={1}>
        <Grid
          item
          container
          xs="auto"
          spacing={2}
          justifyContent="center"
          alignItems="center"
          flexWrap="nowrap"
          borderBottom={({ palette }) => `1px solid ${palette.neutrals.platinum}`}
          display={{ xs: 'inherit', md: 'none' }}
          position="sticky"
          top={0}
          mb={1}
          pb={3}
          zIndex="modal"
          sx={{ backgroundColor: ({ palette }) => palette.neutrals.white }}
        >
          <Grid item xs="auto">
            <Button
              fullWidth={false}
              onClick={handleClearAllOnClick}
              size="small"
              variant="text"
              sx={{ textDecoration: 'none' }}
            >
              <Typography>
                <FormattedMessage id="clear-all" defaultMessage="Clear All" />
              </Typography>
            </Button>
          </Grid>
          <Grid item xs={6} textAlign="center">
            <Typography variant="body1.semibold" component="h2">
              <FormattedMessage id="filters-title" defaultMessage="Filters" />
            </Typography>
          </Grid>
          <Grid item xs="auto">
            <Button
              fullWidth={false}
              onClick={handleCancelOnClick}
              size="small"
              variant="text"
              sx={{ textDecoration: 'none' }}
            >
              <Typography>
                <FormattedMessage id="cancel-button" defaultMessage="Cancel" />
              </Typography>
            </Button>
          </Grid>
        </Grid>
        <Grid item xs="auto">
          <DrawerSection
            data-e2e="filters-drawer-section"
            flexGrow={1}
            flexShrink={1}
            minHeight={0}
            sx={{ overflowY: 'auto' }}
          >
            <FilterSection p={{ xs: 3, md: 6 }} sx={{ backgroundColor: ({ palette }) => palette.neutrals.milk }}>
              <FilterSectionHeader>
                <FormattedMessage id="featured" defaultMessage="Featured" />
              </FilterSectionHeader>
              <FilterSectionContent>
                {featuredFilters.map((featuredFilter, idx) => {
                  const { label } = featuredFilter;

                  const handleOnClick = () => {
                    setFeaturedFilterIdx(idx);

                    track(EVENTS.OFFERING_FILTER_CLICKED, {
                      filter_name: convertToTaxonomyCase(intl.formatMessage(label)),
                    });
                  };

                  return (
                    <OfferingFilterChip
                      className={clsx({ active: featuredFilterIdx === idx })}
                      data-e2e="slideout-featured-filter"
                      clickable
                      label={<FormattedMessage {...label} />}
                      onClick={handleOnClick}
                      key={idx}
                    />
                  );
                })}
              </FilterSectionContent>
            </FilterSection>
            {isAuthenticated && (
              <FilterSection>
                <FilterSectionHeader>
                  <FormattedMessage id="ownership" defaultMessage="Ownership" />
                </FilterSectionHeader>
                <FilterSectionContent>
                  <CombinableFilterOptions filterKey="isOwned" options={ownership} />
                </FilterSectionContent>
              </FilterSection>
            )}
            <FilterSection>
              <FilterSectionHeader>
                <FormattedMessage id="availability" defaultMessage="Availability" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <CombinableFilterOptions filterKey="availability" options={availability} />
              </FilterSectionContent>
            </FilterSection>
            <FilterSection
              sx={{
                border: ({ palette }) => `1px solid ${palette.neutrals.platinum}`,
                borderWidth: ['1px', 0, '1px', 0],
              }}
            >
              <FilterSectionHeader>
                <FormattedMessage id="locations" defaultMessage="Locations" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <MarketDropdown />
              </FilterSectionContent>
            </FilterSection>
            <FilterSection>
              <FilterSectionHeader>
                <FormattedMessage id="rent-status" defaultMessage="Rent Status" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <CombinableFilterOptions options={leaseStatus} filterKey="leaseStatus" />
              </FilterSectionContent>
            </FilterSection>
            <FilterSection>
              <FilterSectionHeader>
                <FormattedMessage id="common.investment.strategy" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <CombinableFilterOptions Component={PropertyTypeFilterChip} options={assetType} filterKey="assetType" />
              </FilterSectionContent>
            </FilterSection>
            <FilterSection>
              <FilterSectionHeader>
                <FormattedMessage id="return-type" defaultMessage="Return Type" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <CombinableFilterOptions options={tagId} filterKey="tagId" />
              </FilterSectionContent>
            </FilterSection>
            <FilterSection>
              <FilterSectionHeader>
                <FormattedMessage id="leverage" defaultMessage="Leverage" />
              </FilterSectionHeader>
              <FilterSectionContent>
                <CombinableFilterOptions options={leverage} filterKey="leverage" />
              </FilterSectionContent>
            </FilterSection>
          </DrawerSection>
        </Grid>
        <Grid
          item
          xs="auto"
          container
          justifyContent={{ xs: 'center', md: 'inherit' }}
          alignItems="center"
          borderTop={({ palette }) => `1px solid ${palette.neutrals.platinum}`}
          position="sticky"
          bottom={0}
          mt={1}
          py={3}
          pl={{ xs: 3, md: 4 }}
          pr={{ xs: 3, md: 6 }}
          zIndex="modal"
          sx={{ backgroundColor: ({ palette }) => palette.neutrals.white }}
        >
          <Grid item xs="auto" display={{ xs: 'none', md: 'flex', flex: '0 1 auto' }}>
            <Button fullWidth={false} onClick={handleClearAllOnClick} sx={{ px: 2 }} variant="text">
              <FormattedMessage id="clear-all" defaultMessage="Clear All" />
            </Button>
          </Grid>
          <Grid item sx={{ flex: { xs: '1 1 auto', md: '0 1 auto' }, marginLeft: { xs: 0, md: 'auto' } }}>
            <Button onClick={close} sx={{ maxWidth: { xs: 'none', md: 'inherit' } }}>
              {offeringCount != null ? (
                <FormattedMessage
                  id="show-properties"
                  defaultMessage="Show {count} Properties"
                  values={{ count: offeringCount }}
                />
              ) : (
                <FormattedMessage id="loading" defaultMessage="Loading..." />
              )}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Box
        sx={{
          position: 'fixed',
          top: ({ spacing }) => spacing(3),
          right: ({ spacing }) => spacing(4),
          display: { xs: 'none', md: 'block' },
        }}
      >
        <IconButton onClick={close}>
          <Close fontSize="large" />
        </IconButton>
      </Box>
    </Drawer>
  );
};
